import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { openPremiumCurrencyDetailsEvent } from '@root/entities/premium'
import { PremiumCurrencyInfo } from '@root/entities/premium/ui'
import { reatomPurchaseBoosters } from '@root/features/premium/purchase-boosters'
import { useAppRoutes } from '@root/features/routing'
import { useOptionalAtom } from '@root/shared/lib'

import { PaymentDialogSuccess } from '../desktop/common/payment-dialog-success'
import { PaymentDrawerSuccess } from '../mobile/common/payment-drawer-success'

import { BuyPremiumForm } from './buy-premium-form'

import type { DeviceType } from '@blade/ui-kit'
import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium'

type Props = {
  currency: CurrencyModel
  premium: PremiumMetaModel | undefined
  onClose: () => void
  device: DeviceType
}

export const BuyPremium: React.FC<Props> = ({
  currency,
  premium,
  onClose,
  device,
}) => {
  const premiumId = useOptionalAtom(premium?.premiumId)
  const premiumIdRef = useLatest(premiumId)
  const model = useMemo(
    () =>
      reatomPurchaseBoosters(
        { currency, type: 'premium', premiumId: premiumIdRef.current },
        'model',
      ),
    [currency, premiumIdRef],
  )
  const { navigateToPremiumIndex } = useAppRoutes()
  const [requestState] = useAtom(model.requestStateAtom)
  const openPremiumCurrencyDetails = useAction(openPremiumCurrencyDetailsEvent)

  const PaymentSuccess =
    device === 'mobile' ? PaymentDrawerSuccess : PaymentDialogSuccess

  return (
    <Root>
      <PremiumCurrencyInfo premium={premium} currency={currency} />

      {AsyncStateN.isSuccess(requestState) ? (
        <PaymentSuccess
          onEdit={() => {
            navigateToPremiumIndex()
            openPremiumCurrencyDetails({ currencyAddress: currency.address })
            onClose()
          }}
          type="premium"
          transactionHash={requestState.value.transactionHash}
          onClose={() => {
            onClose()
          }}
        />
      ) : (
        <BuyPremiumForm model={model} currency={currency} device={device} />
      )}
    </Root>
  )
}

const Root = styled.div`
  overflow: hidden;
  display: grid;
  gap: 12px;
  padding: 20px 30px;
`
